/**
 * Collapsible file-tree navigation rendered over the `DocsManifest`.
 *
 * Groups flat manifest entries by directory (arbitrary depth), renders
 * directories as expandable nodes and `.md` files as leaves. Auto-expands the
 * ancestors of `selectedPath` and supports full keyboard navigation
 * (Requirement 2).
 *
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-2-docs-tree-navigation
 * @see .kiro/specs/help-docs-panel/design.md — Component 3
 *
 * @packageDocumentation
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import type { DocsManifest, DocsManifestEntry } from "./types";

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

/** Props accepted by {@link DocsTree}. */
export interface DocsTreeProps {
  /**
   * The build-time docs manifest whose {@link DocsManifest.tree} entries are
   * grouped into a directory tree.
   */
  manifest: DocsManifest;

  /**
   * The manifest path of the currently selected doc, e.g.
   * `"api/statements.md"`. When set, the tree highlights the matching leaf
   * and auto-expands every ancestor directory.
   */
  selectedPath?: string;

  /**
   * Invoked when the user activates a leaf. The tree never resolves anchors
   * on its own — it always calls this with `anchor === undefined`; anchor
   * resolution belongs to `DocsSearch` and the deep-link parser.
   */
  onSelect(path: string, anchor?: string): void;
}

// ---------------------------------------------------------------------------
// Internal tree representation
// ---------------------------------------------------------------------------

interface DirNode {
  readonly kind: "dir";
  /** Directory key — the accumulated path from root, e.g. `"api"` or `"a/b"`. */
  readonly key: string;
  /** Directory name — the last path segment, e.g. `"api"`. */
  readonly name: string;
  readonly children: TreeNode[];
}

interface FileNode {
  readonly kind: "file";
  /** Full manifest path, e.g. `"api/statements.md"`. */
  readonly key: string;
  /** Filename with extension, e.g. `"statements.md"`. */
  readonly name: string;
  /** Manifest-derived display title. */
  readonly title: string;
}

type TreeNode = DirNode | FileNode;

interface FlatItem {
  readonly node: TreeNode;
  readonly depth: number;
  readonly parentKey: string | null;
}

// ---------------------------------------------------------------------------
// Tree construction and traversal helpers
// ---------------------------------------------------------------------------

/**
 * Build a nested tree from flat manifest entries. Deterministic sort:
 * directories before files, both alphabetical by name.
 */
function buildTree(entries: readonly DocsManifestEntry[]): TreeNode[] {
  const root: TreeNode[] = [];
  const dirMap = new Map<string, DirNode>();

  const findOrCreateDir = (parts: readonly string[]): TreeNode[] => {
    let currentChildren: TreeNode[] = root;
    let currentPath = "";
    for (const part of parts) {
      currentPath = currentPath === "" ? part : `${currentPath}/${part}`;
      let dir = dirMap.get(currentPath);
      if (dir === undefined) {
        dir = { kind: "dir", key: currentPath, name: part, children: [] };
        dirMap.set(currentPath, dir);
        currentChildren.push(dir);
      }
      currentChildren = dir.children;
    }
    return currentChildren;
  };

  for (const entry of entries) {
    const parts = entry.path.split("/");
    const fileName = parts[parts.length - 1];
    const dirParts = parts.slice(0, -1);
    const container = dirParts.length > 0 ? findOrCreateDir(dirParts) : root;
    container.push({
      kind: "file",
      key: entry.path,
      name: fileName,
      title: entry.title,
    });
  }

  const sortNodes = (nodes: TreeNode[]): void => {
    nodes.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === "dir" ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    for (const n of nodes) {
      if (n.kind === "dir") sortNodes(n.children);
    }
  };
  sortNodes(root);

  return root;
}

/**
 * Return the directory-key ancestors of a manifest path, in root → leaf order.
 * `"api/statements.md"` → `["api"]`. `"a/b/c/x.md"` → `["a", "a/b", "a/b/c"]`.
 */
function ancestorsOf(path: string | undefined): string[] {
  if (path === undefined || path === "") return [];
  const parts = path.split("/");
  parts.pop(); // drop filename
  const ancestors: string[] = [];
  let acc = "";
  for (const p of parts) {
    acc = acc === "" ? p : `${acc}/${p}`;
    ancestors.push(acc);
  }
  return ancestors;
}

/**
 * Flatten a tree to the currently-visible ordered list of items given the
 * set of expanded directory keys.
 */
function flatten(
  nodes: readonly TreeNode[],
  expanded: Set<string>,
): FlatItem[] {
  const out: FlatItem[] = [];
  const visit = (
    list: readonly TreeNode[],
    depth: number,
    parentKey: string | null,
  ): void => {
    for (const node of list) {
      out.push({ node, depth, parentKey });
      if (node.kind === "dir" && expanded.has(node.key)) {
        visit(node.children, depth + 1, node.key);
      }
    }
  };
  visit(nodes, 0, null);
  return out;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Collapsible file-tree navigation over the docs manifest.
 *
 * @remarks
 * - Container carries `role="tree"`, each visible item carries `role="treeitem"`.
 * - Directory nodes expose `aria-expanded`; leaf nodes expose `aria-selected`.
 * - Auto-expands the ancestors of {@link DocsTreeProps.selectedPath} on mount
 *   and whenever `selectedPath` changes.
 * - Keyboard: ArrowUp/ArrowDown move to the previous/next visible item;
 *   ArrowLeft collapses an open directory or jumps to the parent; ArrowRight
 *   expands a closed directory or steps into its first child; Enter activates
 *   (selects a leaf, toggles a directory).
 * - Clicks on a leaf call `onSelect(path, undefined)`; the tree never
 *   resolves an anchor on its own.
 */
export function DocsTree(props: DocsTreeProps): JSX.Element {
  const { manifest, selectedPath, onSelect } = props;

  const tree = useMemo(() => buildTree(manifest.tree), [manifest.tree]);

  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(ancestorsOf(selectedPath)),
  );

  // Auto-expand ancestors on mount and whenever selectedPath changes.
  useEffect(() => {
    if (selectedPath === undefined) return;
    const ancestors = ancestorsOf(selectedPath);
    if (ancestors.length === 0) return;
    setExpanded((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const a of ancestors) {
        if (!next.has(a)) {
          next.add(a);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectedPath]);

  const visible = useMemo(() => flatten(tree, expanded), [tree, expanded]);

  const [focusedKey, setFocusedKey] = useState<string | null>(() => {
    const initialTree = buildTree(manifest.tree);
    const initialExpanded = new Set(ancestorsOf(selectedPath));
    const initialVisible = flatten(initialTree, initialExpanded);
    if (initialVisible.length === 0) return null;
    if (
      selectedPath !== undefined &&
      initialVisible.some((v) => v.node.key === selectedPath)
    ) {
      return selectedPath;
    }
    return initialVisible[0].node.key;
  });

  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const setItemRef =
    (key: string) =>
    (el: HTMLDivElement | null): void => {
      if (el !== null) {
        itemRefs.current.set(key, el);
      } else {
        itemRefs.current.delete(key);
      }
    };

  const focusItem = (key: string): void => {
    const el = itemRefs.current.get(key);
    if (el !== undefined) {
      el.focus();
    }
  };

  const toggleExpanded = (key: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    item: FlatItem,
  ): void => {
    const idx = visible.findIndex((v) => v.node.key === item.node.key);
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        if (idx >= 0 && idx < visible.length - 1) {
          focusItem(visible[idx + 1].node.key);
        }
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        if (idx > 0) {
          focusItem(visible[idx - 1].node.key);
        }
        break;
      }
      case "ArrowRight": {
        e.preventDefault();
        if (item.node.kind === "dir") {
          if (!expanded.has(item.node.key)) {
            toggleExpanded(item.node.key);
          } else if (item.node.children.length > 0) {
            focusItem(item.node.children[0].key);
          }
        }
        break;
      }
      case "ArrowLeft": {
        e.preventDefault();
        if (item.node.kind === "dir" && expanded.has(item.node.key)) {
          toggleExpanded(item.node.key);
        } else if (item.parentKey !== null) {
          focusItem(item.parentKey);
        }
        break;
      }
      case "Enter": {
        e.preventDefault();
        if (item.node.kind === "file") {
          onSelect(item.node.key, undefined);
        } else {
          toggleExpanded(item.node.key);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <div
      role="tree"
      aria-label="Documentation tree"
      className="p-2 space-y-0.5 text-sm"
    >
      {visible.map((item) => {
        const isFocused = focusedKey === item.node.key;
        const paddingLeft = `${item.depth * 12 + 8}px`;

        if (item.node.kind === "dir") {
          const isExpanded = expanded.has(item.node.key);
          const dirNode = item.node;
          return (
            <div
              key={dirNode.key}
              ref={setItemRef(dirNode.key)}
              role="treeitem"
              aria-expanded={isExpanded}
              aria-selected={false}
              tabIndex={isFocused ? 0 : -1}
              className="flex items-center gap-1 py-1 px-2 rounded cursor-pointer hover:bg-gray-100"
              style={{ paddingLeft }}
              onClick={() => {
                toggleExpanded(dirNode.key);
                setFocusedKey(dirNode.key);
              }}
              onFocus={() => setFocusedKey(dirNode.key)}
              onKeyDown={(e) => handleKeyDown(e, item)}
            >
              <span
                className="text-gray-400 text-xs w-3 inline-block"
                aria-hidden="true"
              >
                {isExpanded ? "▾" : "▸"}
              </span>
              <span className="font-medium">{dirNode.name}/</span>
            </div>
          );
        }

        const fileNode = item.node;
        const isSelected = fileNode.key === selectedPath;
        const selectedClass = isSelected
          ? " bg-primary-50 text-primary-700"
          : "";

        return (
          <div
            key={fileNode.key}
            ref={setItemRef(fileNode.key)}
            role="treeitem"
            aria-selected={isSelected}
            tabIndex={isFocused ? 0 : -1}
            className={`flex items-center gap-1 py-1 px-2 rounded cursor-pointer hover:bg-gray-100${selectedClass}`}
            style={{ paddingLeft }}
            onClick={() => {
              onSelect(fileNode.key, undefined);
              setFocusedKey(fileNode.key);
            }}
            onFocus={() => setFocusedKey(fileNode.key)}
            onKeyDown={(e) => handleKeyDown(e, item)}
          >
            <span
              className="text-gray-400 text-xs w-3 inline-block"
              aria-hidden="true"
            />
            <span className="font-medium">{fileNode.title}</span>
            <span className="text-xs text-gray-500 ml-1">{fileNode.name}</span>
          </div>
        );
      })}
    </div>
  );
}
